/**
 * User Form Hook
 * 
 * Custom hook that encapsulates user form logic, data transformations,
 * and change detection. This hook can be reused across different user
 * management components.
 * 
 * @module useUserForm
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import UserTransformationService from '../services/user-transformation.service';
import ChangeDetectionService from '../services/change-detection.service';

/**
 * Custom hook for user form management
 * 
 * @param {Object} user - User data for editing (null for create)
 * @param {Function} onSubmit - Submit callback function
 * @returns {Object} Form management utilities
 */
export const useUserForm = (user, onSubmit) => {
  const [transformedInitialValues, setTransformedInitialValues] = useState(() => {
    // For new users, initialize with default structure once
    if (!user) {
      return {
        name: '',
        email: '',
        phoneNumber: '',
        role: '',
        facilityIds: [],
        departmentIds: [],
        facilityDepartments: [{ facilityId: '', departmentIds: [] }],
        isActive: true,
      };
    }

    // For edit mode, we'll populate this via transformAPIToForm
    return null;
  });
  const [originalApiData, setOriginalApiData] = useState(null);
  const [facilitiesDepartments, setFacilitiesDepartments] = useState({});
  const [loadingStates, setLoadingStates] = useState({});

  /**
   * Transform form data and detect changes for submission
   */
  const handleFormSubmit = useCallback(async (formData) => {
    try {
      // Transform form data to API format
      const currentApiData = UserTransformationService.transformFormToAPI(formData);
      
      let submitData;
      
      if (user && originalApiData) {
        // Edit mode: detect and send only changes
        const changedFields = ChangeDetectionService.detectChanges(originalApiData, currentApiData);
        
        if (!ChangeDetectionService.hasChanges(originalApiData, currentApiData)) {
          return; // No changes, skip update
        }
        
        submitData = changedFields;
      } else {
        // Create mode: send all data
        submitData = currentApiData;
      }
      
      await onSubmit(submitData);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error;
    }
  }, [onSubmit, user, originalApiData]);

  /**
   * Load and transform user data for editing
   */
  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        try {
          const transformed = await UserTransformationService.transformAPIToForm(
            user, 
            setFacilitiesDepartments
          );
          setTransformedInitialValues(transformed);
          
          // Store original API data for change detection
          const originalApi = UserTransformationService.transformFormToAPI(transformed);
          setOriginalApiData(originalApi);
        } catch (error) {
          console.error('Error loading user data:', error);
          // Fallback to original user data with minimal structure
          const fallbackData = {
            ...user,
            facilityDepartments: [{ facilityId: '', departmentIds: [] }]
          };
          setTransformedInitialValues(fallbackData);
          
          const originalApi = UserTransformationService.transformFormToAPI(fallbackData);
          setOriginalApiData(originalApi);
        }
      } else {
        // For new users, clear any original API snapshot
        setOriginalApiData(null);
      }
    };

    loadUserData();
  }, [user]);

  /**
   * Get initial form values with fallback
   * CRITICAL: Ensure null values don't break form validation
   */
  const initialFormValues = useMemo(() => {
    if (transformedInitialValues) {
      return {
        ...transformedInitialValues,
        // Force null values to empty arrays to prevent form validation issues
        facilityIds: transformedInitialValues.facilityIds || [],
        departmentIds: transformedInitialValues.departmentIds || [],
      };
    }

    return {
      name: '',
      email: '',
      phoneNumber: '',
      role: '',
      facilityIds: [],
      departmentIds: [],
      facilityDepartments: [{ facilityId: '', departmentIds: [] }],
      isActive: true,
    };
  }, [transformedInitialValues]);

  return {
    initialFormValues,
    facilitiesDepartments,
    setFacilitiesDepartments,
    loadingStates,
    setLoadingStates,
    handleFormSubmit,
    transformedInitialValues
  };
};

export default useUserForm;